import React from 'react'
import { Text, View } from '@react-pdf/renderer'
import {
  BAD,
  Callout,
  CountTable,
  InPairs,
  DataTable,
  GOOD,
  INK,
  LINE,
  MUTED,
  NAVY,
  Prose,
  Quote,
  ReportPage,
  SectionHeading,
  SectionTitle,
  StatRow,
  TEAL,
  WARN,
  clean,
  fmtDate,
  fmtPct,
  topEntries,
} from '../../../../../../components/PDF/pdfKit'
import { themes } from '../../themeList'
import { INTERVALS } from './ControlRoom'

export const PatientFeedback = ({ number, data, chrome, include }) => {
  const p = data.feedback.patients
  const part = (key) => include.part('patientFeedback', key)
  const measures = [
    ['Treated with dignity and respect', p.measures.dignityRespect],
    ['Staff were kind and helpful', p.measures.kindHelpful],
    ['Involved in decisions about care', p.measures.involved],
    ['Confidence and trust in staff', p.measures.confidenceTrust],
  ].filter(([, v]) => v)
  return (
    <ReportPage chrome={chrome}>
      <SectionHeading
        number={number}
        title="Patient compliments and complaints"
        intro="Feedback from patients whose care record is linked to this event, usually given through the feedback text sent after treatment."
      />
      <StatRow
        items={[
          { label: 'Responses', value: p.responses },
          ...(part('counts')
            ? [
                { label: 'Compliments', value: p.compliments, color: GOOD },
                { label: 'Complaints', value: p.complaints, color: p.complaints ? BAD : GOOD },
              ]
            : []),
          ...(part('recommend') ? [{ label: 'Would recommend', value: fmtPct(p.recommendRate), color: TEAL }] : []),
        ]}
      />
      {part('ratings') && measures.length ? (
        <>
          <SectionTitle>How patients rated their care</SectionTitle>
          <DataTable
            columns={[
              { label: 'Statement', flex: 3 },
              { label: 'Answered', flex: 1, align: 'right' },
              { label: 'Agreed', flex: 1, align: 'right' },
            ]}
            rows={measures.map(([label, v]) => [label, v.answered, fmtPct(v.rate)])}
          />
        </>
      ) : null}
      {part('comments') && p.comments.length ? (
        <>
          <SectionTitle>In their words</SectionTitle>
          {p.comments.map((c, i) => (
            <Quote key={i} text={c.text} meta={part('counts') ? c.kind : null} />
          ))}
          <Text style={{ fontSize: 6.5, color: MUTED }}>
            Only comments the patient agreed may be shared are quoted.
          </Text>
        </>
      ) : null}
    </ReportPage>
  )
}

// Feedback-link responses are also appended to the PEA organiser notes as
// "Feedback from ..." paragraphs; they're shown structured above, so only
// the planner's own notes are repeated here.
const organiserNotes = (text, hasResponses) =>
  String(text ?? '')
    .split(/\n{2,}/)
    .map((p) => p.trim())
    .filter((p) => p && !(hasResponses && p.startsWith('Feedback from ')))
    .join('\n\n')

export const OrganiserFeedback = ({ number, data, chrome, pea, include }) => {
  const o = data.feedback.organiser
  const part = (key) => include.part('organiserFeedback', key)
  const notes = part('notes') ? organiserNotes(pea?.clientFeedback, o.responses > 0) : ''
  return (
    <ReportPage chrome={chrome}>
      <SectionHeading number={number} title="Event organiser feedback" />
      {o.responses ? (
        <>
          <StatRow
            items={[
              ...(part('ratings') ? [{ label: 'Overall rating', value: o.overall !== null ? `${o.overall} / 5` : '-', color: TEAL }] : []),
              { label: 'Responses', value: `${o.responses} of ${o.requested}` },
              ...(part('bookAgain')
                ? [{ label: 'Would book again', value: o.bookAgain.Yes || 0, sub: o.bookAgain.Maybe ? `${o.bookAgain.Maybe} maybe` : null, color: GOOD }]
                : []),
            ]}
          />
          <InPairs
            blocks={[
              part('ratings') && (
                <>
                  <SectionTitle marginTop={4}>Ratings out of 5</SectionTitle>
                  <CountTable rows={o.ratings.filter((r) => r.average !== null).map((r) => [r.label, r.average])} />
                </>
              ),
              part('bookAgain') && (
                <>
                  <SectionTitle marginTop={4}>Would book again</SectionTitle>
                  <CountTable rows={topEntries(o.bookAgain)} total={o.responses} />
                </>
              ),
            ]}
          />
          {part('comments') && o.comments.map((c, i) => (
            <View key={i} style={{ marginTop: 8 }} wrap={false}>
              <Text style={{ fontSize: 7, color: MUTED, marginBottom: 3 }}>
                {clean([c.role, c.overall && part('ratings') ? `rated ${c.overall}/5` : null, fmtDate(c.submittedAt)].filter(Boolean).join(' - '))}
              </Text>
              {c.wentWell ? <Quote text={c.wentWell} meta="What went well" /> : null}
              {c.couldImprove ? <Quote text={c.couldImprove} meta="What could be improved" /> : null}
            </View>
          ))}
        </>
      ) : null}
      {notes ? (
        <>
          <SectionTitle>{o.responses ? 'Further feedback' : 'Feedback received'}</SectionTitle>
          <Prose text={notes} />
        </>
      ) : null}
    </ReportPage>
  )
}

export const StaffFeedback = ({ number, data, chrome, pea, include }) => {
  const s = data.feedback.staff
  const part = (key) => include.part('staffFeedback', key)
  return (
    <ReportPage chrome={chrome}>
      <SectionHeading
        number={number}
        title="Staff feedback"
        intro="What our own team said at the end of their shifts. Comments are anonymous."
      />
      {part('summary') && pea?.staffFeedback?.trim() ? (
        <>
          <SectionTitle marginTop={0}>Summary</SectionTitle>
          <Prose text={pea.staffFeedback} />
        </>
      ) : null}
      {part('comments') && s.comments.length ? (
        <>
          <SectionTitle>End-of-shift comments</SectionTitle>
          {s.comments.map((c, i) => (
            <Quote key={i} text={c} />
          ))}
          {s.more ? (
            <Text style={{ fontSize: 6.5, color: MUTED }}>{`A further ${s.more} comments are held on the event.`}</Text>
          ) : null}
        </>
      ) : null}
    </ReportPage>
  )
}

export const Lessons = ({ number, chrome, pea, include }) => {
  // Each theme is its own part, so a planner can keep one internal
  const rows = themes.filter(({ dbName }) => include.part('lessons', dbName))
  return (
    <ReportPage chrome={chrome}>
      <SectionHeading
        number={number}
        title="Lessons and recommendations"
        intro="Our debrief of the event, theme by theme, with what we recommend for next time."
      />
      {rows.map(({ title, dbName }) => (
        <View
          key={dbName}
          style={{ borderWidth: 0.5, borderColor: LINE, borderRadius: 4, marginTop: 7 }}
        >
          <View
            style={{ backgroundColor: NAVY, paddingVertical: 4, paddingHorizontal: 8, borderTopLeftRadius: 4, borderTopRightRadius: 4 }}
            wrap={false}
            minPresenceAhead={40}
          >
            <Text style={{ fontSize: 8, color: '#FFFFFF', fontFamily: 'Helvetica-Bold' }}>{clean(title)}</Text>
          </View>
          <View style={{ flexDirection: 'row', padding: 8 }}>
            <View style={{ flex: 1, paddingRight: 8 }}>
              <Text style={{ fontSize: 6, color: MUTED, fontFamily: 'Helvetica-Bold', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>
                Analysis
              </Text>
              <Text style={{ fontSize: 7.5, color: INK, lineHeight: 1.4 }}>{clean(pea[dbName]?.analysis || '-')}</Text>
            </View>
            <View style={{ flex: 1, paddingLeft: 8, borderLeftWidth: 0.5, borderLeftColor: LINE }}>
              <Text style={{ fontSize: 6, color: TEAL, fontFamily: 'Helvetica-Bold', textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 2 }}>
                Recommendation
              </Text>
              <Text style={{ fontSize: 7.5, color: INK, lineHeight: 1.4 }}>{clean(pea[dbName]?.recommendations || '-')}</Text>
            </View>
          </View>
        </View>
      ))}
    </ReportPage>
  )
}

export const ActionPlan = ({ number, data, chrome }) => {
  const done = data.actions.filter((a) => a.completedAt).length
  return (
    <ReportPage chrome={chrome}>
      <SectionHeading
        number={number}
        title="Action plan"
        intro={`${data.actions.length} action${data.actions.length === 1 ? '' : 's'} arising from the debrief; ${done} complete.`}
      />
      <DataTable
        columns={[
          { label: 'Action', flex: 2.5 },
          { label: 'How', flex: 2.5 },
          { label: 'Owner', flex: 1.2 },
          { label: 'Due', flex: 0.9 },
          { label: 'Status', flex: 1 },
        ]}
        rows={data.actions.map((a) => [
          a.what,
          a.how || '-',
          a.owner || '-',
          fmtDate(a.when) || '-',
          a.completedAt ? `Done ${fmtDate(a.completedAt)}` : a.when && new Date(a.when) < new Date() ? 'Overdue' : 'Open',
        ])}
        boldFirst={false}
      />
    </ReportPage>
  )
}

const QualityRow = ({ label, value, good }) => (
  <View style={{ flexDirection: 'row', justifyContent: 'space-between', paddingVertical: 2.5, borderBottomWidth: 0.5, borderBottomColor: LINE }} wrap={false}>
    <Text style={{ fontSize: 7.5, color: INK }}>{clean(label)}</Text>
    <Text style={{ fontSize: 7.5, fontFamily: 'Helvetica-Bold', color: good ? GOOD : WARN }}>{value}</Text>
  </View>
)

export const Appendix = ({ number, data, chrome, include }) => {
  const q = data.completeness
  const part = (key) => include.part('appendix', key)
  return (
    <ReportPage chrome={chrome}>
      <SectionHeading number={number} title="Methodology and data quality" />
      {part('method') && (
      <>
      <SectionTitle marginTop={0}>How the figures are worked out</SectionTitle>
      <Prose
        size={7.5}
        text={[
          'Patient figures count every electronic and paper patient record, first aid and welfare record linked to the event. Each record is one presentation; a patient seen twice counts twice. Records that have been archived are excluded.',
          `Days are operational days in UK time, running from ${String(data.method.dayRolloverHour).padStart(2, '0')}:00 to ${String(data.method.dayRolloverHour).padStart(2, '0')}:00 the next morning. A record is placed by its incident time, or by when it was created if no incident time was entered.`,
          'Patient information is reported as totals only. No names, dates of birth or clinical notes appear in this report.',
          'Time intervals are reported as the median (the middle value), the mean (the average) and the 90th centile (90% of incidents were at or under this). An interval is left out where either time is missing, the times are in the wrong order, or the gap is more than 24 hours.',
        ].join('\n\n')}
      />
      {data.controlRoom ? (
        <DataTable
          columns={[{ label: 'Interval', flex: 1.3 }, { label: 'Definition', flex: 3.5 }]}
          rows={INTERVALS.map(([, label, definition]) => [label, definition])}
        />
      ) : null}
      </>
      )}

      {part('quality') && (
      <>
      <SectionTitle marginTop={part('method') ? 14 : 0}>Completeness of the underlying records</SectionTitle>
      <QualityRow label="Patient records included" value={q.records} good />
      <QualityRow label="Records not yet submitted (draft)" value={q.draftRecords} good={!q.draftRecords} />
      <QualityRow label="Records with no outcome" value={q.noOutcome} good={!q.noOutcome} />
      <QualityRow label="Records placed by creation time (no incident time)" value={q.timeFromCreated} good={!q.timeFromCreated} />
      <QualityRow label="Paper records (limited detail)" value={q.paperRecords} good={!q.paperRecords} />
      {q.cadIncompleteTimings !== null ? (
        <QualityRow label="CAD incidents with no on-scene time" value={q.cadIncompleteTimings} good={!q.cadIncompleteTimings} />
      ) : null}
      {q.cadNoResource !== null ? (
        <QualityRow label="CAD incidents with no resource sent" value={q.cadNoResource} good={!q.cadNoResource} />
      ) : null}
      {q.staysStillOpen !== null ? (
        <QualityRow label="Treatment stays never closed off" value={q.staysStillOpen} good={!q.staysStillOpen} />
      ) : null}
      {include.allowed('overview', 'shiftFill') ? (
        <QualityRow label="Shifts left unfilled" value={q.unfilledShifts} good={!q.unfilledShifts} />
      ) : null}
      {q.draftRecords ? (
        <Callout title="Draft records" tone={WARN}>
          {clean(
            `${q.draftRecords} record${q.draftRecords === 1 ? ' was' : 's were'} still in draft when this report was generated. They are included, but their details may change once they are completed.`,
          )}
        </Callout>
      ) : null}
      </>
      )}
    </ReportPage>
  )
}
